// Shared renderer/editing leaf; source automatic derivation remains in slide-auto.
export interface StorySlide {
  /** ★9종 = MSB LAYOUT_COMPONENTS 키와 1:1 + 클라 확장 1종(kineticTypo) = SLIDE_COMPONENTS 10종
   *  (Overlay169 가 동적 조회·미지 타입 가드). 감지(detectSlide)는 기존 4종만 파생 — 나머지
   *  5종(checkList/cardGrid/stepFlow/splitFocus/mediaFocus)은 📐설계도(blueprint) 브릿지의 "명시 주입"
   *  전용(디렉터 설계 재현). 🔤🎞 kineticTypo 는 브릿지가 첫·마지막 M씬의 짧은 fullText 를 승격한
   *  «모션 타이포» — 디렉터 어휘 밖(EF 무개조)·props 는 fullText 와 동일(title·sub). */
  type:
    | "bigNumber" | "statRow" | "comparePair" | "fullText"
    | "checkList" | "cardGrid" | "stepFlow" | "splitFocus" | "mediaFocus"
    | "kineticTypo"
    /* 🪧 포스터 — 그림이 화면을 채우고 그 «위»에 글자가 층층이 쌓인다(벤치 v015 실측 번역).
       `mediaFocus`(액자 + 아래 캡션 한 줄)와 배치가 반대라 별개 종류다. 명시 주입 전용. */
    | "posterStack"
    /* 🏗 [건설] 사고 사례 브리핑 3종(2026-10-09 · 정답지 «굴착기 버킷 낙하 v1») — kineticTypo 와 같은 «클라 확장»
       (디렉터 어휘 밖 · EF 무개조). 아래 `CONSTRUCTION_SLIDE_TYPES` 머리말이 계약 정본. */
    | "incidentBrief" | "preventionCard" | "tbmCard"
    /* 🏗🌅 «아침 안전 조회» 틀 전용 6종(화면 전용 · `morning-layouts.tsx` · 틀 조립기만 만든다 · 디렉터·정밀 편집 밖). */
    | "morningCover" | "morningChecks" | "morningWeather" | "morningFocus" | "morningCase" | "morningActions";
  /** 해당 레이아웃 컴포넌트의 데이터 props(colors/f 제외) — layouts.tsx 계약과 1:1. */
  content: Record<string, unknown>;
}

/* ─── 🏗 [건설] 사고 사례 브리핑 장표 3종 — 칸 계약(정본) ─────────────────────────────
 * 칸 이름 = 기획·연출 사실 추출 키(`incident-facts.ts` IncidentFacts: when·where·industry·kind·preventions)를 그대로 쓴다.
 * 값은 추출 원문(`facts.*.value`)을 요약·바꿔 쓰기 없이 싣는다 — ★렌더러는 받은 글자를 바꾸지 않는다★(자르기·말줄임 0 ·
 * 칸에 안 들어가면 덩어리째 글자 크기만 줄인다). 아래 `CONSTRUCTION_SLIDE_LIMITS` 는 «이 안이면 정답지 크기 그대로»인
 * 권장값이지 자르는 선이 아니다. 배열 개수 상한만 편집 계약이 지킨다(넘치는 항목은 편집에서 안 받는다).
 *  · incidentBrief(사고 개요 패널) — 바탕 위 오른쪽 패널 · 빨간 꼬리표 · 제목 · 라벨-값 줄(일시·장소·업종·재해 유형).
 *  · preventionCard(예방대책 카드) — 같은 패널 · 노란 꼬리표(«예방대책 01») · 제목 · 노란 점 목록.
 *  · tbmCard(TBM 카드) — 어두운 바탕 + 줄무늬 띠 + 크림 카드 · 확인 질문 · 체크 칸 · 출처 한 줄(원문 그대로).
 * «사고 순간» 칸은 없다(기획·연출 계약 §0 표 #4). */

/** 사고 개요 패널 칸. 네 줄 칸 중 빈 칸은 줄째 빠진다 · 제목이 비면 제목 줄도 빠진다(꼬리표 아래 바로 줄). */
export interface IncidentBriefContent { tag?: string; title?: string; when?: string; where?: string; industry?: string; kind?: string }
/** 예방대책 카드 칸. 제목이 비면 제목 줄이 빠진다. */
export interface PreventionCardContent { tag?: string; title?: string; preventions: string[] }
/** TBM 카드 칸. */
export interface TbmCardContent { tag?: string; title?: string; checks: string[]; source?: string }

export const CONSTRUCTION_SLIDE_TYPES: ReadonlySet<string> = new Set(["incidentBrief", "preventionCard", "tbmCard"]);

/** 사고 개요 줄 순서 · 라벨(코드 고정 글). */
export const INCIDENT_ROW_KEYS = ["when", "where", "industry", "kind"] as const;
export const INCIDENT_ROW_LABELS: Readonly<Record<(typeof INCIDENT_ROW_KEYS)[number], string>> = {
  when: "일시", where: "장소", industry: "업종", kind: "재해 유형",
};

/** 칸이 비었을 때 화면에 쓰는 글. */
export const CONSTRUCTION_SLIDE_DEFAULTS = {
  incidentBrief: { tag: "사고 개요" },
  preventionCard: { tag: "예방대책" },
  tbmCard: { tag: "TBM 체크", title: "오늘 작업 전 확인" },
} as const;

/** 권장 글자 수(정답지 크기 그대로 들어가는 선) · 배열 개수 상한. */
export const CONSTRUCTION_SLIDE_LIMITS = {
  tag: 10,
  title: 22,
  incidentValue: 30,
  preventions: { max: 4, chars: 30 },
  checks: { max: 4, chars: 28 },
  source: 60,
} as const;

/** 🔤🎞 «모션 타이포 ↔ 전체 문구» 토글이 성립하는 짝 — 같은 동치 클래스라 서로 오갈 수 있다.
 *  ★이 밖의 타입 전환은 열지 않는다★ — 진짜 구조 변경이라 지문이 옳게 막아야 하는 자리다. */
export const MOTION_TOGGLE_TYPES: ReadonlySet<string> = new Set(["fullText", "kineticTypo"]);

/** 🔤🎞 모션 타이포 제목 상한 — 제목 글자 크기가 hero 고정(kinetic-typo.tsx)이라 이보다 길면
 *  줄바꿈이 쌓여 잘린다. 벤치마크도 «한 문장»이다. */
export const KINETIC_TYPO_TITLE_MAX = 24;

/** 이 제목이 모션 타이포로 성립하는가 — 자동 승격(msb-blueprint.promoteKineticTypo)과 수동 토글
 *  (SceneSlidesSection)이 **같은 술어**를 쓴다. 규칙이 둘로 갈리면 «자동은 되는데 손으로는 안 되는»
 *  칸이 생긴다. */
export const canKineticTypo = (title: string): boolean =>
  !!title && !title.includes("\n") && title.length <= KINETIC_TYPO_TITLE_MAX;

